import React, {useEffect, useState} from 'react'
import {Box, TextField, Typography} from '@mui/material'
import AppDialog from 'src/components-new/feedback/AppDialog'
import Button from 'src/components-new/primitives/Button'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import SelectNew from 'src/components-new/inputs/SelectNew'
import {CONTROL_HEIGHT} from 'src/theme-new/tokens'
import {STATUS} from 'src/constants/constants'
import apiClient from 'src/services/apiClient'

interface MaterialOption {
  _id: string
  name: string
  code: string
  amount?: number
  quantityInputType?: string
  quantityOptions?: (string | number)[]
}

export interface MaterialRequestFormValues {
  materialId: string
  materialName: string
  code: string
  requestedQuantity: number | string
}

interface MaterialRequestDialogNewProps {
  open: boolean
  onClose: () => void
  onSave: (values: MaterialRequestFormValues) => void | Promise<void>
}

/**
 * Creation-only dialog — new-style equivalent of
 * src/components/templates/MaterialRequestDialog/MaterialRequestDialog.tsx. There is no
 * readOnly/detail mode, matching the legacy behavior (the list has no row-detail action).
 */
export default function MaterialRequestDialogNew({
  open,
  onClose,
  onSave,
}: MaterialRequestDialogNewProps): JSX.Element {
  const [materials, setMaterials] = useState<MaterialOption[]>([])
  const [statusLoad, setStatusLoad] = useState<string>(STATUS.PENDING)
  const [materialId, setMaterialId] = useState('')
  const [quantity, setQuantity] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open) {
      setMaterialId('')
      setQuantity('')
      setSaving(false)
      apiClient.getRequestableMaterials({}, setStatusLoad).then((data: any) => {
        if (Array.isArray(data)) setMaterials(data)
      })
    }
  }, [open])

  const selectedMaterial = materials.find(m => m._id === materialId)
  const materialOptions = materials.map(m => ({value: m._id, label: m.name}))
  const isSelectQuantity = selectedMaterial?.quantityInputType === 'SELECT'
  const quantityOptions = (selectedMaterial?.quantityOptions ?? []).map(v => ({value: String(v), label: String(v)}))

  const numericQuantity = Number(quantity)
  const isQuantityValid = isSelectQuantity
    ? Boolean(quantity)
    : Boolean(quantity) && !isNaN(numericQuantity) && numericQuantity >= 1 &&
      (selectedMaterial?.amount === undefined || numericQuantity <= selectedMaterial.amount)

  const isValid = Boolean(selectedMaterial) && isQuantityValid

  const handleMaterialChange = (value: string) => {
    setMaterialId(value)
    setQuantity('')
  }

  const handleConfirm = async () => {
    if (!isValid || !selectedMaterial || saving) return
    setSaving(true)
    try {
      await onSave({
        materialId: selectedMaterial._id,
        materialName: selectedMaterial.name,
        code: selectedMaterial.code,
        requestedQuantity: isSelectQuantity ? quantity : numericQuantity,
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <AppDialog
      actions={
        <>
          <Button disabled={saving} onClick={onClose} variant="secondary">Annulla</Button>
          <Button disabled={!isValid} loading={saving} onClick={handleConfirm} variant="primary">
            Richiedi
          </Button>
        </>
      }
      maxWidth="sm"
      onClose={onClose}
      open={open}
      title="Richiedi materiale"
    >
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, pt: 1}}>
        {statusLoad === STATUS.REJECTED && (
          <InlineMessage severity="error">
            {'Errore nel caricare i materiali disponibili.'}
          </InlineMessage>
        )}

        <SelectNew
          disabled={statusLoad === STATUS.PENDING}
          fullWidth
          label="Materiale"
          onChange={handleMaterialChange}
          options={materialOptions}
          placeholder="Seleziona un materiale"
          required
          value={materialId}
        />

        {selectedMaterial && (
          isSelectQuantity ? (
            <SelectNew
              fullWidth
              label="Quantità"
              onChange={setQuantity}
              options={quantityOptions}
              placeholder="Seleziona quantità"
              required
              value={quantity}
            />
          ) : (
            <Box>
              <Typography
                sx={{fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 12, mb: 0.5}}
              >
                {'Quantità *'}
              </Typography>
              {/* TextFieldNew has no numeric variant (only text/email/tel/password), so a
                  plain MUI TextField is used here for the number input, same pattern as
                  the "Avvisami" field in PatientEventDialogNew.tsx. */}
              <TextField
                fullWidth
                inputProps={{min: 1, max: selectedMaterial.amount}}
                onChange={e => setQuantity(e.target.value)}
                size="small"
                sx={{'& .MuiOutlinedInput-root': {height: CONTROL_HEIGHT}}}
                type="number"
                value={quantity}
              />
              {selectedMaterial.amount !== undefined && (
                <Typography sx={{mt: 0.5}} variant="caption" color="text.secondary">
                  {`Quantità massima selezionabile: ${selectedMaterial.amount}`}
                </Typography>
              )}
            </Box>
          )
        )}
      </Box>
    </AppDialog>
  )
}
